import React, { useState, useEffect } from "react";
import { format, differenceInHours } from "date-fns";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import { API_URL, API_URL_UPLOADS } from "./config";

const Trips = () => {
  const [trips, setTrips] = useState([]);
  const [filteredTrips, setFilteredTrips] = useState([]);
  const [orderDateFrom, setOrderDateFrom] = useState("");
  const [orderDateTo, setOrderDateTo] = useState("");
  const [commercialValueCategory, setCommercialValueCategory] = useState("all");
  const [tripStatus, setTripStatus] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [timeInterval, setTimeInterval] = useState("all");
  const [tripCount, setTripCount] = useState(0); // New state for trip count

  useEffect(() => {
    fetch(`${API_URL}/trip`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        setTrips(data);
        setFilteredTrips(data);
      })
      .catch((error) => console.error("Error fetching data:", error));
  }, []);

  useEffect(() => {
    let filtered = [...trips];

    // Date filter
    if (orderDateFrom && orderDateTo) {
      filtered = filtered.filter((trip) => {
        const tripDate = new Date(trip.request_start_datetime);
        const fromDate = new Date(orderDateFrom);
        const toDate = new Date(orderDateTo);
        return tripDate >= fromDate && tripDate <= toDate;
      });
    }

    // Commercial value delivery category filter
    if (commercialValueCategory !== "all") {
      filtered = filtered.filter(
        (trip) =>
          trip.commercial_value_delivery_category === commercialValueCategory
      );
    }

    // Status filter
    if (tripStatus !== "all") {
      filtered = filtered.filter((trip) => trip.status === tripStatus);
    }

    // Channel filter (which app/bot the request came from — see migrations/add_channel_attribution.sql)
    if (channelFilter !== "all") {
      filtered = filtered.filter((trip) =>
        channelFilter === "unknown" ? !trip.channel : trip.channel === channelFilter
      );
    }

    // Time interval filter (merged in from the now-deleted TripsByTime.js — same duplicate
    // trip-list-trio screen, this was its only feature Trips.js didn't already have)
    if (timeInterval !== "all") {
      const now = new Date();
      filtered = filtered.filter((trip) => {
        const tripDate = new Date(trip.request_start_datetime);
        const hoursElapsed = differenceInHours(now, tripDate);
        switch (timeInterval) {
          case "2hrs":
            return hoursElapsed <= 2;
          case "8hrs":
            return hoursElapsed <= 8;
          case "24hrs":
            return hoursElapsed <= 24;
          case "2days":
            return hoursElapsed <= 48;
          case "7days":
            return hoursElapsed <= 168;
          case "14days":
            return hoursElapsed <= 336;
          case "30days":
            return hoursElapsed <= 720;
          default:
            return true; // "overtime" or "all"
        }
      });
    }

    setFilteredTrips(filtered);
    setTripCount(filtered.length); // Update trip count when filtered trips change
  }, [
    orderDateFrom,
    orderDateTo,
    commercialValueCategory,
    tripStatus,
    channelFilter,
    timeInterval,
    trips,
  ]);

  const statuses = ["all", ...new Set(trips.map((trip) => trip.status))];
  const channels = ["all", "web_app", "xgobot", "mobile_app", "unknown"];
  const timeIntervals = [
    "all",
    "2hrs",
    "8hrs",
    "24hrs",
    "2days",
    "7days",
    "14days",
    "30days",
    "overtime",
  ];

  const commercialCategories = [
    "all",
    ...new Set(
      trips
        .map((trip) => trip.commercial_value_delivery_category)
        .filter(Boolean)
    ),
  ];

  return (
    <DashboardLayout>
      <div className="p-6 bg-white dark:bg-gray-800 min-h-screen">
        <h1 className="text-3xl font-bold mb-6 text-gray-800 dark:text-white">
          Trip Details
        </h1>

        {/* Trip Count Card */}
        <div className="mb-6 bg-blue-100 border border-blue-300 text-blue-800 text-center p-4 rounded-lg">
          <strong>{tripCount}</strong> trips displayed
        </div>

        {/* Filters */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Order Date From
            </label>
            <input
              type="date"
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={orderDateFrom}
              onChange={(e) => setOrderDateFrom(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Order Date To
            </label>
            <input
              type="date"
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={orderDateTo}
              onChange={(e) => setOrderDateTo(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Commercial Value Category
            </label>
            <select
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={commercialValueCategory}
              onChange={(e) => setCommercialValueCategory(e.target.value)}
            >
              {commercialCategories.map((category) => (
                <option key={category} value={category}>
                  {category === "all" ? "All Categories" : category}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Status
            </label>
            <select
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={tripStatus}
              onChange={(e) => setTripStatus(e.target.value)}
            >
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status === "all" ? "All Statuses" : status}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Channel
            </label>
            <select
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
            >
              {channels.map((channel) => (
                <option key={channel} value={channel}>
                  {channel === "all" ? "All Channels" : channel}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Time Interval
            </label>
            <select
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={timeInterval}
              onChange={(e) => setTimeInterval(e.target.value)}
            >
              {timeIntervals.map((interval) => (
                <option key={interval} value={interval}>
                  {interval === "all" ? "All Times" : interval}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto shadow-md rounded-lg">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
            <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-300">
              <tr>
                <th className="px-6 py-3">Trip ID</th>
                <th className="px-6 py-3">Customer ID</th>
                <th className="px-6 py-3">Request Start</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Delivery Details</th>
                <th className="px-6 py-3">Distance</th>
                <th className="px-6 py-3">Payment Type</th>
                <th className="px-6 py-3">Channel</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrips.map((trip) => (
                <tr
                  key={trip.trip_id}
                  className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  <td className="px-6 py-4">{trip.trip_id}</td>
                  <td className="px-6 py-4">{trip.cust_id}</td>
                  <td className="px-6 py-4">
                    {trip.request_start_datetime
                      ? format(
                          new Date(trip.request_start_datetime),
                          "MMM dd, yyyy HH:mm"
                        )
                      : "N/A"}
                  </td>
                  <td className="px-6 py-4">{trip.status}</td>
                  <td className="px-6 py-4">{trip.deliveray_details}</td>
                  <td className="px-6 py-4">{trip.distance} km</td>
                  <td className="px-6 py-4">{trip.payment_type}</td>
                  <td className="px-6 py-4">{trip.channel || "unknown"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* No results message */}
        {filteredTrips.length === 0 && (
          <div className="text-center py-4 text-gray-500 dark:text-gray-400">
            No trips found matching the selected filters.
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Trips;
